/**
 * Provider Link Service
 *
 * Connects third-party accounts (GitHub, GitLab, Google, Atlassian, ...) to the
 * signed-in user.
 *
 * As of tonight's architecture decision, litellm's own MCP server catalog + OAuth
 * linking is the FULL replacement source of truth for this — not mia-flow-be's
 * `/api/account/providers/*` (provider-token-service-backed), which this service
 * used to call. This talks to mia-flow-bff's `/api/connectors/*` routes instead
 * (routes/litellm-connectors.js), which proxy/mediate litellm so this frontend
 * never talks to litellm directly and the litellm proxy master key never reaches
 * the browser. mia-flow-be's old routes and provider-token-service are untouched
 * and still exist, just no longer wired to this page.
 *
 * The external contract of this module (checkLinkStatus/redirectToLinking/
 * unlinkProvider/checkPostLinkingReturn) is unchanged on purpose, so Connectors.jsx
 * needed no rewrite — `provider` is still a plain alias string (e.g. "atlassian",
 * "gitlab", "catalog_mcp"), which mia-flow-bff resolves against litellm's real
 * server_id/alias/server_name.
 *
 * KNOWN GAP (tonight, local dev): litellm only has `demo_console_mcp_server` and
 * `catalog_mcp` registered right now — none of the git-provider aliases
 * Connectors.jsx still renders cards for (atlassian/gitlab/github/azure-devops/
 * bitbucket/google). Those cards will call checkLinkStatus for aliases litellm
 * doesn't know about; the BFF 404s and this service treats that as "not linked"
 * (see `notRegistered` on the returned status) rather than an error, so the page
 * degrades to showing them all disconnected instead of breaking. Registering
 * those vendors as litellm MCP servers (or reworking Connectors.jsx to render
 * GET /api/connectors dynamically instead of a hardcoded card per alias) is out
 * of scope for tonight.
 */

import config from '../config.js';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { devLog } from '../utils/devLog';

/** Logged once per page load (console.info) when the BFF has no LiteLLM configured. */
export const CONNECTORS_NEED_LITELLM_MESSAGE =
  'Connectors need LiteLLM: set LITELLM_BASE_URL and LITELLM_MASTER_KEY on mia-flow-bff';

/**
 * sessionStorage key remembering, for the browser tab's session, that the BFF said "connectors not configured":
 * a reload then skips the /api/connectors/* call (and its 503 in the console) and only logs the one info line.
 * A new tab asks again, so turning LiteLLM on is picked up without clearing anything.
 */
export const CONNECTORS_UNAVAILABLE_SESSION_KEY = 'flow.connectors.unavailable';

function readSessionUnavailable() {
  try {
    const raw = window.sessionStorage.getItem(CONNECTORS_UNAVAILABLE_SESSION_KEY);
    return raw ? (JSON.parse(raw)?.reason || 'not configured') : null;
  } catch {
    return null;
  }
}

function writeSessionUnavailable(reason) {
  try {
    window.sessionStorage.setItem(CONNECTORS_UNAVAILABLE_SESSION_KEY, JSON.stringify({ reason }));
  } catch {
    // no storage (private mode, tests): the page-load cache still applies
  }
}

/**
 * True when a /api/connectors/* response means "connectors are switched off here"
 * (mia-flow-bff answers 503 "LITELLM_BASE_URL/LITELLM_MASTER_KEY not configured"
 * when litellm isn't wired up), rather than a real failure.
 */
export function isConnectorsUnavailableResponse(status, body) {
  if (status === 503) return true;
  const message = body && typeof body.error === 'string' ? body.error : '';
  return /not configured/i.test(message);
}

class ProviderLinkService {
  constructor() {
    this._resetAvailability({ keepSession: true });
  }

  /**
   * Forget the cached connectors availability (tests only; a page load starts fresh). `keepSession` keeps the tab's
   * remembered answer, which is what a reload does.
   */
  _resetAvailability({ keepSession = false } = {}) {
    if (!keepSession) {
      try { window.sessionStorage.removeItem(CONNECTORS_UNAVAILABLE_SESSION_KEY); } catch { /* no storage */ }
    }
    // null = not asked yet; { available: true } or { available: false, reason }.
    this._availability = null;
    // Logged at most once per page load.
    this._loggedUnavailable = false;
    // The first request of the page load, which the others wait for so that
    // an unavailable backend is asked once, not once per provider.
    this._probe = null;
  }

  /**
   * Whether the connectors backend answered "not configured" during this page load.
   * @returns {boolean}
   */
  isUnavailable() {
    if (this._availability === null) {
      // An earlier page load of this tab already learned it: no request, just the one info line.
      const remembered = readSessionUnavailable();
      if (remembered) this._markUnavailable(remembered, { persist: false });
    }
    return this._availability?.available === false;
  }

  _markUnavailable(reason, { persist = true } = {}) {
    if (!this._loggedUnavailable) {
      this._loggedUnavailable = true;
      // eslint-disable-next-line no-console
      console.info(CONNECTORS_NEED_LITELLM_MESSAGE);
    }
    this._availability = { available: false, reason };
    if (persist) writeSessionUnavailable(reason);
  }

  _unavailableStatus() {
    return {
      linked: false,
      unavailable: true,
      reason: this._availability?.reason,
      user: null,
    };
  }

  /**
   * Run `request` with the page-load availability cache: the first call goes
   * out alone, the concurrent ones wait for it, and once the backend has said
   * "not configured" no further request is made — `onUnavailable` answers instead.
   */
  async _withAvailability(request, onUnavailable) {
    if (this._availability === null && this._probe) {
      await this._probe.catch(() => {});
    }
    if (this.isUnavailable()) {
      return onUnavailable();
    }
    if (this._availability === null && !this._probe) {
      const probe = request();
      this._probe = probe;
      try {
        return await probe;
      } finally {
        if (this._availability === null) {
          // The probe failed for another reason: let the next call ask again.
          this._probe = null;
        }
      }
    }
    return request();
  }

  /**
   * Read a non-ok response; if it means "connectors not configured here",
   * cache that and return true.
   */
  async _checkUnavailable(response) {
    if (response.ok) {
      if (this._availability === null) this._availability = { available: true };
      return false;
    }
    const body = await (response.clone ? response.clone() : response).json().catch(() => ({}));
    if (isConnectorsUnavailableResponse(response.status, body)) {
      this._markUnavailable(body.error || `HTTP ${response.status}`);
      return true;
    }
    return false;
  }

  /**
   * Refresh the authtool-bff session by calling GET /bff/session.
   * This is equivalent to what happens on a full page refresh — authtool-bff
   * re-validates the login token and creates a fresh internal session.
   * Without this, retrying a failed request hits the same dead session.
   */
  async _refreshSession() {
    try {
      const response = await fetch('/bff/session', {
        method: 'GET',
        credentials: 'include',
      });
      devLog(`🔄 [ProviderLinkService] Session refresh: HTTP ${response.status}`);
      return response.ok;
    } catch (err) {
      console.warn('⚠️ [ProviderLinkService] Session refresh failed:', err.message);
      return false;
    }
  }

  /**
   * Check if a provider/connector is linked for the signed-in user.
   *
   * Proxies to `GET {bffUrl}/api/connectors/{provider}/status`
   * (routes/litellm-connectors.js), which in turn calls litellm's
   * `GET /v1/mcp/server/{server_id}/oauth-user-credential/status` using the
   * caller's own identity. A 401 (the BFF couldn't resolve the caller's own
   * session/token yet) is retried with the same session-refresh back-off the
   * old provider-token-service-backed implementation used. A 404 (litellm has
   * no server matching this alias) is NOT an error — it just means this
   * connector isn't registered in litellm, so it is reported as not linked.
   *
   * @param {string} provider - Connector alias (e.g., 'atlassian', 'catalog_mcp')
   * @param {object} [_opts] - Internal options (used by retry loop)
   * A 503 / "not configured" answer means connectors are switched off in this
   * environment: it is cached for the page load (no further requests, no
   * console errors) and reported as `{ linked: false, unavailable: true }`.
   *
   * @returns {Promise<object>} { linked, connectedAt, expiresAt, isExpired, notRegistered?, unavailable? }
   */
  async checkLinkStatus(provider, _opts = {}) {
    const MAX_RETRIES = 3;

    const attempt = async (retryCount) => {
      try {
        const response = await getAuthorizedFetch()(
          `${config.bffUrl}/api/connectors/${encodeURIComponent(provider)}/status`,
          {
            method: 'GET',
            headers: getBffHeaders(),
          }
        );

        if (response.status === 404) {
          if (this._availability === null) this._availability = { available: true };
          return { linked: false, notRegistered: true };
        }

        if (await this._checkUnavailable(response)) {
          return this._unavailableStatus();
        }

        if (response.status === 401 && retryCount < MAX_RETRIES) {
          console.warn(
            `⚠️ [ProviderLinkService] Unauthenticated resolving ${provider} status, ` +
            `refreshing session then retrying (attempt ${retryCount + 1}/${MAX_RETRIES})…`
          );
          await this._refreshSession();
          return attempt(retryCount + 1);
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to check ${provider} link status`);
        }

        const data = await response.json();
        // No per-user profile info comes back from litellm's credential-status
        // call (unlike the old provider-token-service response) — `user` stays
        // null; Connectors.jsx only ever reads `.linked` from it today.
        return { ...data, user: null };
      } catch (error) {
        console.error(`Error checking ${provider} link status:`, error);
        throw error;
      }
    };

    return this._withAvailability(() => attempt(0), () => this._unavailableStatus());
  }

  /**
   * Ask mia-flow-bff where to send the browser to link a connector.
   *
   * Proxies to `POST {bffUrl}/api/connectors/{provider}/link`, which resolves
   * the litellm server, runs Dynamic Client Registration against litellm's
   * `/{server_name}/register` proxy (cached after the first call), and returns
   * a real `authorization_url` pointing at litellm's `/{server_name}/authorize`
   * proxy with a `redirect_uri` back to mia-flow-bff's own OAuth callback route
   * (`GET /api/connectors/{serverId}/callback`), which completes the token
   * exchange and finally 302s the browser back to `redirectUri` below.
   *
   * @param {string} provider - Connector alias (e.g., 'atlassian', 'catalog_mcp')
   * @param {string} redirectUri - Where to return once linking completes
   * @returns {Promise<object>} Object with authorization_url
   */
  async initiateLinking(provider, redirectUri) {
    try {
      const baseUrl = config.bffUrl || '';
      const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const urlString = `${cleanBase}/api/connectors/${encodeURIComponent(provider)}/link`;
      const url = new URL(urlString, window.location.origin);

      if (redirectUri) {
        url.searchParams.append('redirect_uri', redirectUri);
      }

      const response = await getAuthorizedFetch()(url.toString(), {
        method: 'POST',
        headers: getBffHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to initiate ${provider} linking`);
      }

      const data = await response.json();

      devLog('🔗 Linking - backend response:');
      devLog('   Provider:', provider);
      devLog('   Authorization URL:', data.authorization_url);

      return data;
    } catch (error) {
      console.error(`Error initiating ${provider} linking:`, error);
      throw error;
    }
  }

  /**
   * List available connectors known to litellm (not scoped to "linked").
   * Proxies to `GET {bffUrl}/api/connectors`. Nothing in this codebase calls
   * this today (Connectors.jsx still renders a hardcoded card per provider),
   * kept for API-shape compatibility with the pre-litellm implementation.
   * @returns {Promise<object>} Object with providers array and count (and `unavailable` when switched off)
   */
  async listLinkedProviders() {
    const unavailable = () => ({ providers: [], count: 0, unavailable: true });
    return this._withAvailability(async () => {
      try {
        const response = await getAuthorizedFetch()(
          `${config.bffUrl}/api/connectors`,
          {
            method: 'GET',
            headers: getBffHeaders(),
          }
        );

        if (await this._checkUnavailable(response)) {
          return unavailable();
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to list connectors');
        }

        const data = await response.json();
        return { providers: data.connectors || [], count: (data.connectors || []).length };
      } catch (error) {
        console.error('Error listing connectors:', error);
        throw error;
      }
    }, unavailable);
  }

  /**
   * Unlink a provider/connector from the user's account.
   * Proxies to `DELETE {bffUrl}/api/connectors/{provider}/link`.
   * @param {string} provider - Connector alias (e.g., 'atlassian', 'catalog_mcp')
   * @returns {Promise<object>} Result object
   */
  async unlinkProvider(provider) {
    try {
      const response = await getAuthorizedFetch()(
        `${config.bffUrl}/api/connectors/${encodeURIComponent(provider)}/link`,
        {
          method: 'DELETE',
          headers: getBffHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to unlink ${provider}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error unlinking ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Start a linking flow with a full-page redirect to the provider.
   *
   * @param {string} provider - Provider alias (e.g., 'github', 'gitlab')
   * @param {string} redirectUri - Where to redirect after linking completes
   * @returns {Promise<void>}
   */
  async redirectToLinking(provider, redirectUri) {
    try {
      const result = await this.initiateLinking(provider, redirectUri);

      if (!result.authorization_url) {
        throw new Error('No authorization URL returned');
      }

      devLog(`🔗 Redirecting to the ${provider} linking flow...`);
      devLog(`   Return URI: ${redirectUri}`);
      devLog(`   Authorization URL: ${result.authorization_url}`);

      // Store provider info and return URI for post-redirect verification
      sessionStorage.setItem('pending_link_provider', provider);
      sessionStorage.setItem('pending_link_timestamp', Date.now().toString());
      sessionStorage.setItem('pending_link_return_uri', redirectUri);

      window.location.href = result.authorization_url;

    } catch (error) {
      console.error(`Error redirecting to ${provider} linking:`, error);
      throw error;
    }
  }

  /**
   * Check if we just returned from a linking flow
   * @returns {Object|null} Provider info if we just returned from linking, null otherwise
   */
  checkPostLinkingReturn() {
    // mia-flow-bff's own OAuth callback (routes/litellm-connectors.js) finishes
    // the token exchange and 302s the browser back to the return URI with a
    // clean URL, exactly like the old provider-token-service callback did. So
    // all we have to recognise here is our own pending-link marker.
    const provider = sessionStorage.getItem('pending_link_provider');
    const timestamp = sessionStorage.getItem('pending_link_timestamp');

    if (!provider || !timestamp) {
      return null;
    }

    const clear = () => {
      sessionStorage.removeItem('pending_link_provider');
      sessionStorage.removeItem('pending_link_timestamp');
      sessionStorage.removeItem('pending_link_return_uri');
    };

    // Stale marker: the user probably abandoned the flow.
    if (Date.now() - parseInt(timestamp, 10) > 5 * 60 * 1000) {
      clear();
      return null;
    }

    clear();
    return { provider, timestamp };
  }

  /**
   * Open linking flow in popup window (DEPRECATED - use redirectToLinking instead)
   * @deprecated Use redirectToLinking instead
   * @param {string} provider - Provider alias (e.g., 'github', 'gitlab')
   * @param {Function} onComplete - Callback when linking is complete
   * @returns {Promise<void>}
   */
  async openLinkingPopup(provider, onComplete) {
    console.warn('openLinkingPopup is deprecated. Use redirectToLinking instead.');

    // For backward compatibility, redirect to current page
    const redirectUri = window.location.href;

    try {
      await this.redirectToLinking(provider, redirectUri);
      // Note: onComplete won't be called since we're doing a full redirect
    } catch (error) {
      if (onComplete) {
        onComplete(false, { error: error.message });
      }
      throw error;
    }
  }

  /**
   * Check and auto-link provider if not already linked using Client-Initiated Linking
   * @param {string} provider - Provider alias (e.g., 'github', 'gitlab')
   * @param {string} redirectUri - Where to redirect after linking
   * @returns {Promise<boolean>} True if already linked, false if linking initiated
   */
  async ensureLinked(provider, redirectUri = null) {
    try {
      // Check current status
      const status = await this.checkLinkStatus(provider);

      if (status.linked) {
        devLog(`✅ ${provider} is already linked`);
        return true;
      }

      // Not linked, show linking prompt
      const shouldLink = window.confirm(
        `To use ${provider} features, you need to link your ${provider} account.\n\n` +
        `Click OK to link your ${provider} account now. You will be redirected to complete the linking.`
      );

      if (!shouldLink) {
        devLog(`❌ User declined to link ${provider} account`);
        return false;
      }

      // Use current page as redirect if not specified
      const finalRedirectUri = redirectUri || window.location.href;

      // Initiate linking flow with redirect
      await this.redirectToLinking(provider, finalRedirectUri);

      // Note: This won't return because we're redirecting
      return false;

    } catch (error) {
      console.error(`Error ensuring ${provider} is linked:`, error);
      return false;
    }
  }
}

const providerLinkService = new ProviderLinkService();
export default providerLinkService;
